/**
 * One Twitch embed for the whole session. Jumping between channels re-tunes
 * the existing player (`setChannel`) instead of tearing the iframe down and
 * booting a new one, so a switch is near-instant and volume/mute carry over.
 * The embed only accepts commands after its ready event; a switch requested
 * before that is queued and applied the moment it fires.
 */

export interface PlayerLike {
  play(): void;
  setChannel(channel: string): void;
  setMuted(muted: boolean): void;
  getMuted(): boolean;
  setVolume(volume: number): void;
  getVolume(): number;
}

export interface EmbedLike {
  getPlayer(): PlayerLike;
  addEventListener(event: string, callback: () => void): void;
}

export type ShowResult = 'created' | 'switched' | 'queued' | 'noop';

export interface ControllerOptions {
  /** Boots a fresh embed tuned to `channel` */
  create: (channel: string, muted: boolean) => EmbedLike;
  /** Muted startup allows autoplay before the viewer interacts. */
  initialMuted?: boolean;
  /** Twitch.Embed.VIDEO_READY — injected so tests can fire it */
  readyEvent?: string;
  now?: () => number;
  /** How long to wait for the ready event before a switch rebuilds the embed */
  readyGraceMs?: number;
}

/** Twitch's own constant for the embed's ready event */
export const VIDEO_READY = 'video.ready';
/** An embed that has not reported ready in this long is treated as wedged */
export const READY_GRACE_MS = 6000;

export class TwitchPlayerController {
  private embed: EmbedLike | null = null;
  private player: PlayerLike | null = null;
  private current = '';
  private pending: string | null = null;
  private muted: boolean;
  private createdAt = 0;
  private readonly create: ControllerOptions['create'];
  private readonly readyEvent: string;
  private readonly now: () => number;
  private readonly readyGraceMs: number;

  constructor(opts: ControllerOptions) {
    this.create = opts.create;
    this.muted = opts.initialMuted ?? true;
    this.readyEvent = opts.readyEvent ?? VIDEO_READY;
    this.now = opts.now ?? (() => Date.now());
    this.readyGraceMs = opts.readyGraceMs ?? READY_GRACE_MS;
  }

  get channel(): string {
    return this.current;
  }

  show(channel: string): ShowResult {
    // Still no ready event long after boot: the embed is wedged — start over
    if (this.embed && !this.player && channel !== this.current && this.now() - this.createdAt > this.readyGraceMs) {
      this.dispose();
    }

    if (!this.embed) {
      const embed = this.create(channel, this.muted);
      this.embed = embed;
      this.player = null;
      this.current = channel;
      this.pending = null;
      this.createdAt = this.now();
      embed.addEventListener(this.readyEvent, () => {
        if (this.embed !== embed) return;
        this.player = embed.getPlayer();
        this.applyMute();
        if (this.pending && this.pending !== this.current) {
          this.current = this.pending;
          this.player.setChannel(this.current);
        }
        this.pending = null;
        this.play();
      });
      return 'created';
    }

    if (!this.player) {
      if (channel === this.current) {
        this.pending = null;
        return 'noop';
      }
      this.pending = channel;
      return 'queued';
    }

    if (channel === this.current) return 'noop';
    this.current = channel;
    this.player.setChannel(channel);
    this.play();
    return 'switched';
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyMute();
    if (!muted) this.play();
  }

  isMuted(): boolean {
    return this.muted;
  }

  private play(): void {
    try {
      this.player?.play();
    } catch {
      // Twitch's play control remains available if the browser needs a tap.
    }
  }

  private applyMute(): void {
    if (!this.player) return;
    try {
      this.player.setMuted(this.muted);
      // Unmuting into silence is a common Twitch quirk — give it a voice
      if (!this.muted && this.player.getVolume() === 0) this.player.setVolume(0.5);
    } catch {
      // the player can throw while tearing down; nothing to do
    }
  }

  dispose(): void {
    this.embed = null;
    this.player = null;
    this.current = '';
    this.pending = null;
  }
}
